import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useCompetition } from "@/context/CompetitionContext";
import CompetitionSelector from "@/components/CompetitionSelector";
import {
  ChevronRight,
  ChevronLeft,
  FileDown,
  Maximize2,
  Clock,
  Users,
  Rewind,
  Play,
  Pause,
} from "lucide-react";
import ConfrontoIndicator from "@/components/ConfrontoIndicator";
import { FaseBadge } from "@/components/FaseBadge";

/**
 * Painel do Maestro
 * Interface de controle de palco para o operador do evento:
 *  - Bloco central com a categoria ATUAL e a PRÓXIMA
 *  - Botão gigante "AVANÇAR PRÓXIMO"
 *  - Cronograma lateral clicável
 *  - Botão para imprimir Roteiro em PDF (Backstage)
 *  - Cronômetro de palco (start/pause/reset)
 */
export default function Maestro() {
  const { currentId, current } = useCompetition();
  const startTime = current?.horario_inicio || "18:00"; // Vem da competição — Maestro NÃO altera
  const [intervalo, setIntervalo] = useState(0);
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  const [athletesCache, setAthletesCache] = useState({}); // category_id -> athletes[]
  const [confrontos, setConfrontos] = useState({}); // athlete_id -> [confronto entries]
  const [elapsed, setElapsed] = useState(0);
  const [running, setRunning] = useState(false);

  const storageKey = useMemo(
    () => (currentId ? `maestro-idx-${currentId}` : null),
    [currentId]
  );

  const load = useCallback(async () => {
    if (!currentId) return;
    const { data } = await api.get(`/competitions/${currentId}/schedule`, {
      params: { intervalo_min: intervalo },
    });
    setData(data);
  }, [currentId, intervalo]);

  useEffect(() => {
    load();
  }, [load]);

  // restore persisted index on competition change
  useEffect(() => {
    if (!storageKey) return;
    const saved = parseInt(localStorage.getItem(storageKey) || "0", 10);
    setIdx(Number.isFinite(saved) ? saved : 0);
    setElapsed(0);
    setRunning(false);
  }, [storageKey]);

  useEffect(() => {
    if (storageKey) localStorage.setItem(storageKey, String(idx));
  }, [idx, storageKey]);

  // Sync stage state to backend (drives the Backstage TV monitor)
  useEffect(() => {
    if (!currentId) return;
    api
      .post(`/competitions/${currentId}/stage-state`, {
        current_index: idx,
        start_time: startTime,
        intervalo_min: intervalo,
      })
      .catch((e) => console.warn("Falha ao sincronizar palco:", e?.message));
  }, [currentId, idx, startTime, intervalo]);

  // stage timer
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [running]);

  const schedule = data?.schedule || [];
  const currentCat = schedule[idx] || null;
  const nextCat = schedule[idx + 1] || null;

  const loadAthletesFor = useCallback(
    async (catId) => {
      if (!catId || athletesCache[catId]) return;
      try {
        const { data: rows } = await api.get(`/competitions/${currentId}/athletes`, {
          params: { category_id: catId },
        });
        setAthletesCache((prev) => ({ ...prev, [catId]: rows }));
      } catch {
        setAthletesCache((prev) => ({ ...prev, [catId]: [] }));
      }
    },
    [currentId, athletesCache]
  );

  useEffect(() => {
    if (currentCat) loadAthletesFor(currentCat.category_id);
    if (nextCat) loadAthletesFor(nextCat.category_id);
  }, [currentCat, nextCat, loadAthletesFor]);

  // Fetch previous confrontations for the current category
  useEffect(() => {
    if (!currentCat?.category_id) {
      setConfrontos({});
      return;
    }
    let cancel = false;
    api
      .get(`/categories/${currentCat.category_id}/confrontos`)
      .then((r) => {
        if (!cancel) setConfrontos(r.data?.confrontos || {});
      })
      .catch(() => {
        if (!cancel) setConfrontos({});
      });
    return () => {
      cancel = true;
    };
  }, [currentCat?.category_id]);

  const advance = () => {
    if (idx < schedule.length - 1) {
      setIdx((i) => i + 1);
      setElapsed(0);
      setRunning(false);
    }
  };
  const back = () => {
    if (idx > 0) {
      setIdx((i) => i - 1);
      setElapsed(0);
      setRunning(false);
    }
  };
  const jumpTo = (i) => {
    setIdx(i);
    setElapsed(0);
    setRunning(false);
  };

  const printBackstage = () => {
    if (!currentId) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/export/backstage.pdf?competition_id=${currentId}&start_time=${encodeURIComponent(
      startTime
    )}&intervalo_min=${intervalo}`;
    window.open(url, "_blank");
  };

  const openTelao = () => {
    if (!currentId || !currentCat) return;
    window.open(`/telao/${currentId}/${currentCat.category_id}`, "_blank");
  };

  const fmtTimer = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0)
      return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
    return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  };

  const currentAthletes = currentCat
    ? athletesCache[currentCat.category_id] || []
    : [];
  const nextAthletes = nextCat ? athletesCache[nextCat.category_id] || [] : [];

  if (!currentId) {
    return (
      <div>
        <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
          <div>
            <div className="overline mb-2">Controle de Palco</div>
            <h1 className="font-display text-5xl font-black tracking-tight">
              MAESTRO
            </h1>
          </div>
          <CompetitionSelector />
        </div>
        <div className="card p-6 text-center text-[var(--text-secondary)]">
          Selecione uma competição para operar o palco.
        </div>
      </div>
    );
  }

  return (
    <div data-testid="maestro-page">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="overline mb-2">Controle de Palco</div>
          <h1 className="font-display text-5xl font-black tracking-tight">
            MAESTRO
          </h1>
          <div className="text-xs text-[var(--text-secondary)] mt-1">
            {current?.nome}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {data?.formato === "previa_final" && (
            <span data-testid="maestro-formato" className="chip chip-admin">Prévia e Final</span>
          )}
          <button
            data-testid="maestro-open-telao"
            className="btn-ghost flex items-center gap-2"
            onClick={openTelao}
            disabled={!currentCat?.category_id}
            title="Abrir Telão da categoria atual"
          >
            <Maximize2 size={14} /> Telão
          </button>
          <button
            data-testid="maestro-print-roteiro"
            className="btn-primary flex items-center gap-2"
            onClick={printBackstage}
            title="Imprimir Roteiro Completo (PDF)"
          >
            <FileDown size={16} /> Roteiro (PDF)
          </button>
          <CompetitionSelector />
        </div>
      </div>

      {/* Config bar */}
      <div className="card p-4 mb-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <label className="overline block mb-1 flex items-center gap-1">
            Início do evento
            <span className="text-[9px] text-[#C9A84C]" title="Definido no cadastro da competição">
              🔒
            </span>
          </label>
          <input
            data-testid="maestro-start"
            type="time"
            className="input-field opacity-70 cursor-not-allowed"
            value={startTime}
            readOnly
            title="Editável apenas em Competições → Editar"
          />
        </div>
        <div>
          <label className="overline block mb-1">Intervalo (min)</label>
          <input
            data-testid="maestro-interval"
            type="number"
            min={0}
            className="input-field"
            value={intervalo}
            onChange={(e) => setIntervalo(parseInt(e.target.value || 0))}
          />
        </div>
        <div className="stat-card">
          <div className="overline mb-1 flex items-center gap-1">
            <Users size={11} /> Categoria
          </div>
          <div className="stat-number" style={{ fontSize: "1.5rem" }}>
            {schedule.length > 0 ? `${idx + 1} / ${schedule.length}` : "0 / 0"}
          </div>
        </div>
        <div className="stat-card">
          <div className="overline mb-1 flex items-center gap-1">
            <Clock size={11} /> Tempo no Palco
          </div>
          <div
            className="stat-number tabular-nums"
            style={{ fontSize: "1.5rem" }}
            data-testid="maestro-timer"
          >
            {fmtTimer(elapsed)}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* MAIN CONTROL PANEL */}
        <div className="lg:col-span-2 space-y-4">
          {/* Current category card */}
          <div
            className="card p-6 border-t-4"
            style={{ borderTopColor: "#C9A84C" }}
            data-testid="maestro-current-card"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="overline text-[#C9A84C] flex items-center gap-2">
                No Palco Agora <FaseBadge fase={currentCat?.fase} testId="maestro-current-fase" />
              </div>
              <div className="text-xs text-[var(--text-secondary)] tabular-nums">
                {currentCat
                  ? `${currentCat.inicio} → ${currentCat.fim}`
                  : "—"}
              </div>
            </div>
            <div
              className="font-display font-black tracking-tight leading-none"
              style={{ fontSize: "clamp(2rem, 5vw, 3.75rem)" }}
              data-testid="maestro-current-name"
            >
              {currentCat ? currentCat.categoria : "— Sem categorias —"}
            </div>
            {currentCat && currentCat.fase !== "intervalo" && (
              <div className="mt-4 flex flex-wrap gap-4 text-sm text-[var(--text-secondary)]">
                <div>
                  <Users size={13} className="inline mr-1" />
                  {currentCat.atletas} atletas
                </div>
                <div>
                  <Clock size={13} className="inline mr-1" />
                  {currentCat.duracao_min} min previstos
                  {currentCat.detalhe && <span className="text-[11px] ml-1">({currentCat.detalhe})</span>}
                </div>
                <div className="tabular-nums">
                  Ordem #{currentCat.ordem}
                </div>
              </div>
            )}
            {currentCat?.fase === "intervalo" && (
              <div className="mt-4 text-sm text-[var(--text-secondary)]" data-testid="maestro-intervalo-info">
                <Clock size={13} className="inline mr-1" />
                {currentCat.duracao_min} min de pausa entre as prévias e as finais.
              </div>
            )}

            {/* Athletes preview */}
            {currentCat && currentAthletes.length > 0 && (
              <div className="mt-5 pt-4 border-t border-white/5">
                <div className="overline mb-2">Ordem de Chamada</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto pr-1">
                  {currentAthletes.map((a) => (
                    <div
                      key={a.id}
                      className="flex items-center gap-2 text-sm"
                      data-testid={`maestro-athlete-${a.id}`}
                    >
                      <span className="tabular-nums font-display font-bold text-[#C9A84C] w-8">
                        {a.numero}
                      </span>
                      <span className={"truncate " + (a.wo ? "line-through opacity-50" : "")}>{a.nome}</span>
                      {a.wo && (
                        <span data-testid={`maestro-wo-${a.id}`} className="text-[9px] font-black tracking-widest px-1 py-0.5 bg-[#FF453A] text-white shrink-0">
                          W.O.
                        </span>
                      )}
                      <ConfrontoIndicator
                        confrontos={confrontos[a.id]}
                        currentAthleteId={a.id}
                        testId={`maestro-confronto-${a.id}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Advance controls */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <button
              data-testid="maestro-back"
              onClick={back}
              disabled={idx === 0}
              className="btn-ghost flex items-center justify-center gap-2 py-4 disabled:opacity-40"
            >
              <ChevronLeft size={18} /> Voltar
            </button>

            <button
              data-testid="maestro-advance"
              onClick={advance}
              disabled={idx >= schedule.length - 1}
              className="md:col-span-2 flex items-center justify-center gap-3 py-6 font-display font-black text-xl tracking-widest transition disabled:opacity-30"
              style={{
                background: "#C9A84C",
                color: "#0a0a0a",
                boxShadow: "0 0 0 1px #C9A84C, 0 12px 32px rgba(201,168,76,0.25)",
              }}
            >
              AVANÇAR PRÓXIMO <ChevronRight size={22} />
            </button>

            <div className="flex items-center justify-between gap-2">
              <button
                data-testid="maestro-timer-toggle"
                onClick={() => setRunning((r) => !r)}
                className="btn-ghost flex-1 flex items-center justify-center gap-2 py-4"
              >
                {running ? <Pause size={16} /> : <Play size={16} />}
                {running ? "Pausar" : "Iniciar"}
              </button>
              <button
                data-testid="maestro-timer-reset"
                onClick={() => {
                  setElapsed(0);
                  setRunning(false);
                }}
                className="btn-ghost py-4 px-3"
                title="Zerar cronômetro"
              >
                <Rewind size={15} />
              </button>
            </div>
          </div>

          {/* Next up card */}
          <div className="card p-5" data-testid="maestro-next-card">
            <div className="flex items-center justify-between mb-2">
              <div className="overline flex items-center gap-2">
                Próxima Categoria <FaseBadge fase={nextCat?.fase} testId="maestro-next-fase" />
              </div>
              <div className="text-xs text-[var(--text-secondary)] tabular-nums">
                {nextCat ? `Início ${nextCat.inicio}` : "—"}
              </div>
            </div>
            <div
              className="font-display font-black tracking-tight"
              style={{ fontSize: "1.75rem" }}
              data-testid="maestro-next-name"
            >
              {nextCat ? nextCat.categoria : "— Fim do evento —"}
            </div>
            {nextCat && nextCat.fase !== "intervalo" && (
              <div className="mt-2 text-xs text-[var(--text-secondary)]">
                {nextCat.atletas} atletas · {nextCat.duracao_min} min
                {nextAthletes.length > 0 && (
                  <span className="ml-2">
                    · Primeiros:{" "}
                    {nextAthletes
                      .slice(0, 3)
                      .map((a) => a.nome)
                      .join(", ")}
                    {nextAthletes.length > 3 ? "…" : ""}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* SIDE SCHEDULE */}
        <div className="lg:col-span-1">
          <div className="card p-0 overflow-hidden">
            <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
              <div className="overline">Cronograma</div>
              <div className="text-xs text-[var(--text-secondary)]">
                {schedule.length} {data?.formato === "previa_final" ? "etapas" : "cat."}
              </div>
            </div>
            <div className="max-h-[70vh] overflow-y-auto">
              {schedule.length === 0 && (
                <div className="p-4 text-center text-sm text-[var(--text-secondary)]">
                  Nenhuma categoria cadastrada.
                </div>
              )}
              {schedule.map((s, i) => {
                const isCurrent = i === idx;
                const isPast = i < idx;
                return (
                  <button
                    key={s.key || s.category_id}
                    data-testid={`maestro-schedule-row-${i}`}
                    onClick={() => jumpTo(i)}
                    className={
                      "w-full text-left px-4 py-3 border-b border-white/5 transition flex items-center gap-3 " +
                      (isCurrent
                        ? "bg-[#C9A84C]/15"
                        : isPast
                        ? "opacity-40 hover:opacity-70"
                        : "hover:bg-white/5")
                    }
                  >
                    <div
                      className={
                        "tabular-nums font-display font-black text-lg w-8 " +
                        (isCurrent ? "text-[#C9A84C]" : "")
                      }
                    >
                      {s.ordem ?? "—"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div
                        className={
                          "truncate text-sm " +
                          (isCurrent ? "font-bold text-white" : "")
                        }
                      >
                        {s.categoria} <FaseBadge fase={s.fase} />
                      </div>
                      <div className="text-[11px] text-[var(--text-secondary)] tabular-nums">
                        {s.inicio}{s.fase === "intervalo" ? ` · ${s.duracao_min} min` : ` · ${s.atletas} atletas`}
                      </div>
                    </div>
                    {isCurrent && (
                      <span
                        className="text-[10px] font-bold tracking-widest px-1.5 py-0.5"
                        style={{ background: "#C9A84C", color: "#0a0a0a" }}
                      >
                        AO VIVO
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
